import React, { useState, useEffect } from 'react';
import {
    Menu, X, Shield, MapPin, Mail, ChevronRight,
    Users, Briefcase, Zap, Globe, Lock, Eye, Database, Info
} from 'lucide-react';

// --- STYLES ---
const FontLoader = () => (
    <style dangerouslySetInnerHTML={{
        __html: `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap');
    
    :root {
      --font-main: 'Inter', sans-serif;
      --font-mono: 'JetBrains Mono', monospace;
    }
    
    body {
      font-family: var(--font-main);
    }
    
    .font-it {
      font-family: var(--font-mono);
      letter-spacing: -0.02em;
    }
  `}} />
);

// --- COMPONENTS ---

const Header = ({ currentPage, setCurrentPage }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const navItems = [
        { id: 'home', label: 'Home' },
        { id: 'services', label: 'Workflow' },
        { id: 'about', label: 'Platform' },
        { id: 'privacy', label: 'Privacy' },
        { id: 'contact', label: 'Contact' },
    ];

    const handleNavClick = (id) => {
        setCurrentPage(id);
        setIsMobileMenuOpen(false);
        window.scrollTo(0, 0);
    };

    return (
        <header className="bg-white shadow-sm border-b border-slate-100 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-20">
                    <div
                        className="flex-shrink-0 flex items-center cursor-pointer"
                        onClick={() => handleNavClick('home')}
                    >
                        <span className="font-it font-bold text-xl text-blue-950 tracking-tighter">
                            VALETTE<span className="text-orange-500">_</span>LTD
                        </span>
                    </div>

                    <nav className="hidden md:flex space-x-8">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => handleNavClick(item.id)}
                                className={`text-xs uppercase tracking-widest font-it font-bold transition-all duration-200 ${currentPage === item.id
                                        ? 'text-orange-600 border-b-2 border-orange-500 pb-1'
                                        : 'text-slate-500 hover:text-blue-900'
                                    }`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </nav>

                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="text-slate-700 hover:text-blue-800 focus:outline-none"
                        >
                            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </div>

            {isMobileMenuOpen && (
                <div className="md:hidden bg-white border-t border-slate-100 shadow-xl">
                    <div className="px-4 pt-2 pb-6 space-y-1">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => handleNavClick(item.id)}
                                className={`block w-full text-left px-3 py-4 text-sm font-it font-bold border-b border-slate-50 ${currentPage === item.id
                                        ? 'text-orange-600'
                                        : 'text-slate-600'
                                    }`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
};

const Footer = ({ setCurrentPage }) => {
    return (
        <footer className="bg-slate-950 text-slate-400 pt-16 pb-8 border-t-4 border-orange-500">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">

                    <div>
                        <div className="flex items-center mb-6">
                            <span className="font-it font-bold text-xl text-white tracking-tighter uppercase">
                                VALETTE_LTD
                            </span>
                        </div>
                        <p className="text-sm leading-relaxed mb-6 font-light">
                            Premium IT freelance ecosystem. Engineered for elite talent and visionary enterprises.
                        </p>
                    </div>

                    <div>
                        <h3 className="text-white font-it text-sm font-bold mb-6 uppercase tracking-widest">Navigation</h3>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <button onClick={() => setCurrentPage('home')} className="hover:text-orange-400 transition-colors">Home</button>
                            </li>
                            <li>
                                <button onClick={() => setCurrentPage('services')} className="hover:text-orange-400 transition-colors">Workflow</button>
                            </li>
                            <li>
                                <button onClick={() => setCurrentPage('privacy')} className="hover:text-orange-400 transition-colors">Privacy Policy</button>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-white font-it text-sm font-bold mb-6 uppercase tracking-widest">Contact Information</h3>
                        <ul className="space-y-4 text-sm font-light">
                            <li className="flex items-start">
                                <MapPin size={18} className="mr-3 text-orange-500 flex-shrink-0 mt-0.5" />
                                <span>7/F, 111 Bonham Strand, MW TOWER, Sheung Wan, Hong Kong</span>
                            </li>
                            <li className="flex items-center">
                                <Mail size={18} className="mr-3 text-orange-500 flex-shrink-0" />
                                <span className="font-it text-xs tracking-tight text-blue-300">manager@valetteltd.com</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Mandatory Legal Disclaimer */}
                <div className="border-t border-slate-900 pt-8 pb-8">
                    <p className="text-[11px] leading-relaxed text-slate-500 font-light text-center md:text-left">
                        DISCLAIMER: VALETTE LIMITED is not an employment agency and does not provide recruitment or job placement services. This resource is strictly a technological platform for the exchange of contacts between independent specialists and clients. The company does not assume any employment obligations and provides no guarantees of obtaining work or hiring employees. All interactions are carried out by users at their own risk.
                    </p>
                </div>

                <div className="border-t border-slate-900 pt-6 flex flex-col md:flex-row justify-between items-center">
                    <p className="text-xs font-it">
                        &copy; {new Date().getFullYear()} VALETTE LIMITED
                    </p>
                    <div className="flex gap-4 mt-4 md:mt-0">
                        <Shield size={16} className="text-slate-700" />
                       
                    </div>
                </div>
            </div>
        </footer>
    );
};

// --- PAGES ---

const Home = ({ setCurrentPage }) => (
    <div className="animate-fadeIn">
        <section className="bg-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    <div className="lg:w-1/2">
                        <div className="inline-block px-3 py-1 bg-orange-50 text-orange-600 font-it text-xs font-bold rounded-sm mb-6 border border-orange-100 uppercase tracking-widest">
                            v3.0.1 Stable
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold text-blue-950 leading-none mb-8 font-it tracking-tighter">
                            HUB FOR <br />
                            <span className="text-orange-500">EXPERTS</span>
                        </h1>
                        <p className="text-lg text-slate-500 mb-10 leading-relaxed max-w-lg font-light">
                            VALETTE LIMITED is a high-tech platform for direct interaction between IT freelancers and global business. No middlemen, just code and results.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={() => setCurrentPage('contact')}
                                className="bg-blue-950 text-white hover:bg-black px-8 py-4 rounded-sm font-it font-bold text-xs uppercase tracking-widest transition-all shadow-xl"
                            >
                                Find a Specialist
                            </button>
                            <button
                                onClick={() => setCurrentPage('contact')}
                                className="bg-white text-blue-950 border border-slate-200 hover:border-orange-500 px-8 py-4 rounded-sm font-it font-bold text-xs uppercase tracking-widest transition-all"
                            >
                                Expert Registration
                            </button>
                        </div>
                    </div>
                    <div className="lg:w-1/2 w-full relative">
                        <img
                            src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80"
                            alt="[IT Infrastructure in orange and blue tones]"
                            className="rounded-sm shadow-2xl object-cover w-full h-[350px] md:h-[500px] border-l-4 border-orange-500"
                        />
                    </div>
                </div>
            </div>
        </section>

        <section className="py-20 bg-slate-50 border-y border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {[
                        { icon: <Users size={24} />, title: 'Direct Access', desc: 'Direct contacts without intermediary recruitment agencies' },
                        { icon: <Briefcase size={24} />, title: 'Tech Hub', desc: 'Specialized environment tailored for IT project execution' },
                        { icon: <Zap size={24} />, title: 'Fast Matching', desc: 'Instant data exchange between platform participants' },
                        { icon: <Globe size={24} />, title: 'HK Entity', desc: 'Legal reliability of a registered Hong Kong platform' }
                    ].map((item, index) => (
                        <div key={index} className="p-8 bg-white border border-slate-200 rounded-sm hover:shadow-md transition-all">
                            <div className="text-orange-500 mb-6">{item.icon}</div>
                            <h3 className="font-it font-bold text-sm text-blue-950 mb-2 uppercase tracking-tight">{item.title}</h3>
                            <p className="text-xs text-slate-400 font-light leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    </div>
);

const Services = () => (
    <div className="animate-fadeIn pb-20">
        <div className="bg-blue-950 py-24 border-b border-orange-500">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-4xl md:text-6xl font-it font-bold text-white mb-6 tracking-tighter uppercase">Platform <span className="text-orange-500">Logic_</span></h1>
                <p className="text-xl text-blue-200 max-w-2xl font-light">Transparent interaction processes within the Valette ecosystem.</p>
            </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white p-12 border border-slate-100 shadow-sm">
                    <h3 className="text-2xl font-it font-bold mb-8 text-blue-950 uppercase tracking-tighter">Project Posting</h3>
                    <p className="font-light text-slate-600 mb-8">Clients can publish technical requirements and directly contact suitable specialists.</p>
                    <div className="space-y-4 text-sm font-it text-orange-600">
                        <div className="flex items-center gap-2"><ChevronRight size={14} /> Direct Communication</div>
                        <div className="flex items-center gap-2"><ChevronRight size={14} /> No Recruitment Commissions</div>
                    </div>
                </div>
                <div className="bg-slate-900 p-12 text-white">
                    <h3 className="text-2xl font-it font-bold mb-8 text-orange-500 uppercase tracking-tighter">Expert Listing</h3>
                    <p className="font-light text-slate-300 mb-8">Specialists create profiles, confirming their skills and availability for new projects.</p>
                    <div className="space-y-4 text-sm font-it text-blue-400">
                        <div className="flex items-center gap-2"><ChevronRight size={14} /> Global Contracts</div>
                        <div className="flex items-center gap-2"><ChevronRight size={14} /> Direct Access to CTOs</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const About = () => (
    <div className="animate-fadeIn pb-20">
        <div className="bg-slate-50 py-24 border-b border-slate-200 text-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-4xl md:text-5xl font-it font-bold text-blue-950 mb-6 tracking-tighter uppercase">Valette <span className="text-orange-500">Network</span></h1>
                <p className="text-xl text-slate-500 max-w-2xl mx-auto font-light">The technological bridge between talent and objectives.</p>
            </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 text-slate-600 text-lg leading-relaxed space-y-8 font-light">
            <p>
                <strong>VALETTE LIMITED</strong> is a platform built to optimize search and interaction in the Information Technology sector. Our hub in Hong Kong provides a robust infrastructure for professional data exchange.
            </p>
            <div className="bg-white p-8 border-l-4 border-orange-500 shadow-sm">
                <h4 className="font-it font-bold text-blue-950 uppercase text-sm mb-2">Our Status</h4>
                <p className="text-sm">We are not an employer and do not provide employment services. We create a space where professionals find each other without unnecessary barriers.</p>
            </div>
        </div>
    </div>
);

const Privacy = () => (
    <div className="animate-fadeIn pb-20 bg-white">
        <div className="bg-slate-50 py-24 border-b border-slate-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-4xl font-it font-bold text-blue-950 mb-4 tracking-tighter uppercase">Privacy_Policy</h1>
                <p className="text-lg text-slate-400 max-w-2xl font-light font-it uppercase tracking-widest">Valette Limited Platform</p>
            </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-slate-700 leading-relaxed font-light">
            <div className="space-y-12">

                {/* Header Section */}
                <section className="space-y-4">
                    <h2 className="text-2xl font-it font-bold text-blue-950 uppercase tracking-tighter border-l-4 border-orange-500 pl-4">Privacy Policy of VALETTE LIMITED</h2>
                    <p className="text-sm">
                        The purpose of this Privacy Policy regarding personal information (hereinafter also referred to as the "Policy") is to explain the procedure and terms for processing your personal data when you visit websites or use online services that are respectively owned by us or provided by us.
                    </p>
                    <p className="text-sm">
                        Where not contrary to the personal data legislation applicable to our relationship (hereinafter also referred to as the "Applicable Legislation"), this Policy also constitutes the data subject's consent to the processing, including the collection, of personal data.
                    </p>
                    <p className="text-sm">
                        We may periodically update the Policy without special notice to you. We recommend reviewing this page from time to time to track possible changes.
                    </p>
                    <p className="text-sm">
                        VALETTE LIMITED belongs to an ecosystem that includes websites, mobile applications, clients, applets, and other applications designed to provide services, and also includes independently managed platforms, websites, and clients within the ecosystem. This Privacy Policy applies to all platforms, websites, and departments. By using the Services of VALETTE LIMITED, you consent to the collection, storage, processing, and transfer of your personal data as described in this Privacy Policy.
                    </p>
                </section>

                {/* Section 1 */}
                <section className="space-y-4">
                    <h3 className="text-lg font-it font-bold text-blue-950 flex items-center gap-2 uppercase tracking-tight">
                        <Database size={18} className="text-orange-500" /> 01. Collected Information
                    </h3>
                    <p className="text-sm">
                        VALETTE LIMITED collects, processes, and stores personal information that you provide when using our services or after providing your consent. This personal information may include:
                    </p>
                    <ul className="list-disc ml-6 text-sm space-y-2">
                        <li><strong>Electronic identification:</strong> application username, account and password, other information for user identification.</li>
                        <li><strong>Financial data:</strong> bank account details, account status, transaction history, and/or taxpayer identification number.</li>
                        <li><strong>Transaction information:</strong> data on transactions you perform, such as the recipient's name, your name, the transaction amount, and timestamp.</li>
                        <li><strong>Message content:</strong> feedback, emails, SMS messages, app ratings, and comments.</li>
                        <li><strong>Activity and device data:</strong> page views, search history, model, system language, operating system, fingerprint, IP address, MAC address, and identifiers (advertising identifier, IMEI, SSID).</li>
                        <li><strong>Personal details (for individuals):</strong> Email address, mobile phone number, full name, nationality, passport number (or other government ID), date of birth, and residential address.</li>
                        <li><strong>Corporate details (for clients):</strong> Legal company name, registration information, full names of representatives, business description, and information about beneficial owners.</li>
                    </ul>
                </section>

                {/* Section 2 */}
                <section className="space-y-4">
                    <h3 className="text-lg font-it font-bold text-blue-950 flex items-center gap-2 uppercase tracking-tight">
                        <Shield size={18} className="text-orange-500" /> 02. Your Rights
                    </h3>
                    <p className="text-sm">
                        In accordance with Applicable Legislation, you have the following rights:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-it font-bold">
                        <div className="p-4 bg-slate-50 border border-slate-100 uppercase">Right of access</div>
                        <div className="p-4 bg-slate-50 border border-slate-100 uppercase">Right to rectification</div>
                        <div className="p-4 bg-slate-50 border border-slate-100 uppercase">Right to erasure</div>
                        <div className="p-4 bg-slate-50 border border-slate-100 uppercase">Right to object</div>
                        <div className="p-4 bg-slate-50 border border-slate-100 uppercase">Right to withdraw consent</div>
                        <div className="p-4 bg-slate-50 border border-slate-100 uppercase">Right to portability</div>
                    </div>
                    <p className="text-sm mt-4">
                        To exercise these rights, please contact us at <span className="text-orange-600 font-medium">manager@valetteltd.com</span>. We will respond within 30 days (this period may be extended to 60 days in certain cases).
                    </p>
                </section>

                {/* Section 3 */}
                <section className="space-y-4">
                    <h3 className="text-lg font-it font-bold text-blue-950 flex items-center gap-2 uppercase tracking-tight">
                        <Lock size={18} className="text-orange-500" /> 03. Operations & Security
                    </h3>
                    <p className="text-sm">
                        <strong>Law Enforcement:</strong> We may be required by law or court order to disclose your information to law enforcement agencies and other competent authorities.
                    </p>
                    <p className="text-sm">
                        <strong>Information Usage:</strong> Compliance with laws, Fraud detection, Service provision, Research and development, and Marketing activities.
                    </p>
                    <p className="text-sm">
                        <strong>Data Storage:</strong> Your information will be stored on VALETTE LIMITED servers and may be transferred outside the country where it was collected.
                    </p>
                </section>

                {/* Section 4 */}
                <section className="space-y-4">
                    <h3 className="text-lg font-it font-bold text-blue-950 flex items-center gap-2 uppercase tracking-tight">
                        <Eye size={18} className="text-orange-500" /> 04. Cookies
                    </h3>
                    <p className="text-sm">
                        We use cookies to track performance and advertising effectiveness. They record user habits and preferences, but do not extract personal information from the hard drive or email addresses.
                    </p>
                    <p className="text-sm">
                        <strong>Disabling Cookies:</strong> Most browsers are already set to accept cookies, but you can set your browser to reject them. This may limit access to some platform features.
                    </p>
                </section>

                {/* Security Section */}
                <section className="space-y-4 bg-slate-900 text-slate-100 p-8 rounded-sm">
                    <h3 className="text-lg font-it font-bold flex items-center gap-2 uppercase tracking-tight">
                        <Shield size={18} className="text-orange-500" /> Information Security
                    </h3>
                    <p className="text-sm">
                        Your account is secure. Keep your information and password safe. We ensure data protection through backups and encryption, but remember that information protection is not perfect. Safeguard your personal information and report any leak if it occurs.
                    </p>
                    
                </section>

                {/* Final Guarantee Section */}
                <section className="space-y-4 border-t border-slate-100 pt-8">
                    <h3 className="text-sm font-it font-bold text-blue-950 uppercase tracking-widest flex items-center gap-2">
                        <Info size={16} className="text-orange-500" /> User Guarantees
                    </h3>
                    <p className="text-sm">
                        If you choose to provide personal data, you guarantee to us that such data is accurate, complete, reliable, and up-to-date. Unless expressly provided otherwise by Applicable Legislation, we assume no obligation to you to verify the accuracy, completeness, reliability, and currency of the provided data.
                    </p>
                    <p className="text-sm font-medium">
                        By providing personal data of a third party, you also guarantee to us that there is valid consent from such person for the transfer of their personal data to us.
                    </p>
                </section>

            </div>
        </div>
    </div>
);

const Contact = () => (
    <div className="animate-fadeIn pb-20">
        <div className="bg-slate-50 py-24 border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-4xl font-it font-bold text-blue-950 mb-4 tracking-tighter uppercase">Platform_Contact</h1>
                <p className="text-lg text-slate-400 max-w-2xl font-light">Contact VALETTE_LTD administration.</p>
            </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
            <div className="flex flex-col lg:flex-row gap-0 shadow-2xl rounded-sm overflow-hidden">
                <div className="lg:w-1/3 bg-blue-950 text-white p-12">
                    <h3 className="text-xl font-it font-bold mb-10 text-orange-400 uppercase tracking-widest">_Endpoints</h3>
                    <div className="space-y-10">
                        <div className="flex items-start">
                            <MapPin className="text-orange-400 mr-4 mt-1 flex-shrink-0" size={20} />
                            <div>
                                <h4 className="font-it text-xs font-bold uppercase mb-2">Location</h4>
                                <p className="text-sm font-light text-blue-200 leading-relaxed">MW TOWER, 7/F, Hong Kong</p>
                            </div>
                        </div>
                        <div className="flex items-start">
                            <Mail className="text-orange-400 mr-4 mt-1 flex-shrink-0" size={20} />
                            <div>
                                <h4 className="font-it text-xs font-bold uppercase mb-2">Official Email</h4>
                                <p className="text-sm font-light text-blue-200 font-it lowercase">manager@valetteltd.com</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:w-2/3 bg-white p-12">
                    <form className="space-y-8 font-light" onSubmit={(e) => e.preventDefault()}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="text-[10px] font-it font-bold uppercase text-slate-400">Participant Name</label>
                                <input type="text" className="w-full px-0 py-2 border-b border-slate-200 bg-transparent outline-none focus:border-orange-500" placeholder="Username" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-it font-bold uppercase text-slate-400">Request Type</label>
                                <select className="w-full px-0 py-2 border-b border-slate-200 bg-transparent outline-none focus:border-orange-500 font-light">
                                    <option>General Support</option>
                                    <option>Partnership</option>
                                    <option>Data Removal</option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-it font-bold uppercase text-slate-400">Contact Email</label>
                            <input type="email" className="w-full px-0 py-2 border-b border-slate-200 bg-transparent outline-none focus:border-orange-500" placeholder="manager@valetteltd.com" />
                        </div>
                        <button type="submit" className="bg-orange-500 text-white font-it font-bold py-4 px-12 rounded-sm hover:bg-black transition-all uppercase text-xs tracking-widest">
                            Send Request
                        </button>
                    </form>
                </div>
            </div>
        </div>
    </div>
);

// --- MAIN APP COMPONENT ---

export default function App() {
    const [currentPage, setCurrentPage] = useState('home');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentPage]);

    const renderPage = () => {
        switch (currentPage) {
            case 'home': return <Home setCurrentPage={setCurrentPage} />;
            case 'services': return <Services />;
            case 'about': return <About />;
            case 'privacy': return <Privacy />;
            case 'contact': return <Contact />;
            default: return <Home setCurrentPage={setCurrentPage} />;
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-white text-slate-800 antialiased selection:bg-orange-100 selection:text-orange-900">
            <FontLoader />
            <Header currentPage={currentPage} setCurrentPage={setCurrentPage} />
            <main className="flex-grow">
                {renderPage()}
            </main>
            <Footer setCurrentPage={setCurrentPage} />
        </div>
    );
}
